<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="flex items-stretch justify-between mt-2.5">
        <div class="flex space-x-2 element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('HeadContract.CashflowBaseline.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('HeadContract.CashflowBaseline.subTitle') }}
            </p>
          </div>
        </div>

        <div
          class="element-island flex items-center gap-3 flex-wrap justify-end"
        >
          <div
            v-if="contractAmount.adjustments !== 0"
            class="flex flex-col"
          >
            <div
              class="flex justify-between space-x-2"
            >
              <span class="text-sm font-medium text-gray-900">
                Gross Works Value
              </span>

              <span class="text-sm text-gray-900">
                {{ $tcc(contractAmount.work) }}
              </span>
            </div>

            <div
              class="flex justify-between space-x-2"
            >
              <span class="text-sm font-medium text-gray-900">
                Contract Adjustments
              </span>

              <span class="text-sm text-gray-900">
                {{ $tcc(-contractAmount.adjustments) }}
              </span>
            </div>

            <div class="flex justify-between space-x-2">
              <span class="text-sm font-medium text-gray-900">
                Contract Sum
              </span>

              <span class="text-sm text-gray-900">
                {{ $tcc(contractAmount.total) }}
              </span>
            </div>
          </div>

          <div
            v-else
            class="bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm"
          >
            <div class="text-sm text-gray-500">
              Contract Sum
            </div>

            <div class="text-lg font-semibold text-gray-900">
              {{ $tcc(contractAmount.total) }}
            </div>
          </div>
        </div>
      </div>

      <div
        class="element-island overflow-y-auto flex flex-col min-h-max flex-1"
      >
        <div
          class="w-full duration-200 transition-[height] ease-linear"
          style="--animate-duration: 0.2s;"
          :class="[showGraphs ? 'h-96' : 'h-0']"
          @transitionend="graphsHidden"
        >
          <Transition
            enter-active-class="animate__animated animate__fadeIn"
            leave-active-class="animate__animated animate__fadeOut"
          >
            <div
              v-show="showGraphsCanvas"
              class="flex w-full h-full relative"
            >
              <BaselineChart
                ref="chartRef"
                :project="project"
                :trades="trades"
              />
            </div>
          </Transition>
        </div>

        <div class="w-full relative overflow-y-auto flex-1 rounded-md border border-gray-200">
          <div
            class="sticky z-[25] left-2 top-4 w-min"
          >
            <button
              type="button"
              class="group p-1 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              @click="toggleGraph"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="showGraphs"
               
                class="w-5 h-5 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>

              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
               
                class="w-5 h-5 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>
            </button>
          </div>

          <div v-if="fetchTradesAction.is(States.LOADING)">
            <div class="flex flex-col justify-center items-center absolute inset-0">
              <AppSpinner />

              <h3 class="text-sm font-medium text-gray-900 mb-1">
                {{ $t('HeadContract.CashflowBaseline.loadingTrades') }}
              </h3>
            </div>
          </div>

          <div
            v-else-if="workTrades.length > 0"
            class="flex flex-col absolute inset-0"
          >
            <TradeBaselineRow
              v-for="trade in workTrades"
              :key="trade.id"
              :trade="trade"
              @baseline-updated="chartRef?.updateBaselineOverride(trade, $event.newBaselines)"
              @baseline-saved="chartRef?.clearBaseline(trade)"
            />
          </div>

          <div
            v-else
            class="px-6 py-16 text-center"
          >
            <div class="flex flex-col items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-300">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <h3 class="text-sm font-medium text-gray-900 mb-1">
                {{ $t('HeadContract.CashflowBaseline.noTradesFound') }}
              </h3>

              <p class="text-sm text-gray-500">
                {{ $t('HeadContract.CashflowBaseline.addTrades') }}
              </p>

              <RouterLink
                :to="{
                  name: 'BillOfQuantities', params: { projectId: project?.id }
                }"
                class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
</svg>
                {{ $t('HeadContract.CashflowBaseline.button.addTrades') }}
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaselineChart from '@/components/HeadContract/CashflowBaseline/BaselineChart.vue';
import TradeBaselineRow from '@/components/HeadContract/CashflowBaseline/TradeBaselineRow.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const router = useRouter();

watch(project, (newValue) => {
  if(newValue && !newValue.boq_locked) {
    const userStore = useUserStore();

    if(userStore.isRole(['contract_manager'], false) && newValue.boq_locked) {
      router.push({ name: 'BillOfQuantities', params: { projectId: newValue.id } });
    }
  }
}, { immediate: true });

const tradesStore = useTradesStore();
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);

watch(() => props.projectId, () => {
  fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
    console.log(error);
  });
}, { immediate: true });

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items', (query) => {
      query.whereNull('parent_id');
    })
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', 'direct')
    .orderBy('sort_order')
    .whereHas('trade_items')
    .get();
});

const workTrades = computed(() => {
  return trades.value.filter((trade) => trade.totalBudget > 0);
});

const contractAmount = computed(() => {
  const contractAmount = {
    total: 0,
    work: 0,
    adjustments: 0,
  };

  trades.value.forEach((trade) => {
    contractAmount.total += trade.totalAdjustedBudget;
    contractAmount.work += trade.totalBudget;

    contractAmount.adjustments += trade.totalBudget - trade.totalAdjustedBudget;
  });

  return contractAmount;
});

const chartRef = useTemplateRef('chartRef');
const showGraphs = ref(true);
const showGraphsCanvas = ref(true);
const setFullHeight = ref(false);

const toggleGraph = () => {
  if(showGraphs.value) {
    showGraphsCanvas.value = false;
    showGraphs.value = false;
    setFullHeight.value = true;
  } else {
    if(setFullHeight.value) {
      showGraphs.value = true;
      setFullHeight.value = false;
    }
  }
};

const graphsHidden = () => {
  if(showGraphs.value) {
    showGraphsCanvas.value = true;
  } else {
    showGraphsCanvas.value = false;
  }
};
</script>

<style scoped></style>
